/**
 * "今天动态"（与 iOS Home/TodayFeed.swift 一致）：提醒确认、任务完成、获得卡牌、兑换申请、积分变化，按时间倒序。
 * 当天的积分流水按来源归到对应动态上，没有归属的（手动调整、成就、集齐系列、兑换扣分等）单独列出。
 */
import { useMemo } from "react";
import { signed, useCalendar, useStore, type Child, type JSONValue, type PointLedgerEntry } from "../../core";
import { C, categoryStyle, rarityStyle } from "../../ui";

interface FeedItem {
  id: string;
  at: number;
  color: string;
  text: string;
  points?: number | null;
}

function durationSec(v: JSONValue | null | undefined): number | null {
  if (v && typeof v === "object" && !Array.isArray(v)) {
    const d = v["duration_sec"];
    if (typeof d === "number") return d;
  }
  return null;
}

export function TodayFeed({ child }: { child: Child }) {
  const cal = useCalendar();
  const reminderLogs = useStore((s) => s.reminderLogs);
  const assignments = useStore((s) => s.assignments);
  const contents = useStore((s) => s.contents);
  const childCards = useStore((s) => s.childCards);
  const cards = useStore((s) => s.cards);
  const redemptions = useStore((s) => s.redemptions);
  const pointLedger = useStore((s) => s.pointLedger);
  const today = cal.today();

  const items = useMemo(() => {
    const isToday = (ms: number | null | undefined) => ms != null && cal.dateString(ms) === today;

    let ledger: PointLedgerEntry[] = Object.values(pointLedger).filter((e) => e.childId === child.id && isToday(e.createdAt));
    const takePoints = (match: (e: PointLedgerEntry) => boolean): number | null => {
      const hit = ledger.filter(match);
      if (!hit.length) return null;
      ledger = ledger.filter((e) => !match(e));
      return hit.reduce((n, e) => n + e.delta, 0);
    };

    const out: FeedItem[] = [];

    for (const log of Object.values(reminderLogs)) {
      if (log.childId !== child.id) continue;
      if (log.ackedAt != null && isToday(log.ackedAt)) {
        const pts = takePoints((e) => e.sourceType === "reminder" && (e.sourceId === log.occurrenceKey || e.sourceId === log.id));
        out.push({ id: `ack-${log.id}`, at: log.ackedAt, color: C.success, text: `已确认提醒「${log.title || log.text}」`, points: pts });
      } else if (log.escalatedAt != null && isToday(log.escalatedAt)) {
        out.push({ id: `esc-${log.id}`, at: log.escalatedAt, color: C.danger, text: `提醒「${log.title}」没有人确认` });
      } else if (log.kind === "instant" && log.createdAt != null && isToday(log.createdAt)) {
        const who = log.createdByName ? `${log.createdByName}发送` : "已发送";
        out.push({ id: `ins-${log.id}`, at: log.createdAt, color: C.disabled, text: `${who}提醒「${log.text}」· 等待确认` });
      }
    }

    for (const a of Object.values(assignments)) {
      if (a.childId !== child.id || a.status !== "completed" || a.completedAt == null || !isToday(a.completedAt)) continue;
      let text = `完成「${contents[a.contentId]?.title ?? "学习任务"}」`;
      const sec = durationSec(a.result) ?? durationSec(a.progress);
      if (sec != null && sec > 0) text += `用时 ${Math.max(1, Math.floor(sec / 60))} 分钟`;
      const pts = takePoints((e) => (e.sourceType === "assignment" || e.sourceType === "on_time") && e.sourceId === a.id);
      out.push({ id: `asg-${a.id}`, at: a.completedAt, color: categoryStyle("course").dot, text, points: pts });
    }

    for (const cc of Object.values(childCards)) {
      if (cc.childId !== child.id || !isToday(cc.obtainedAt)) continue;
      const card = cards[cc.cardId];
      if (!card) continue;
      const who = cc.actorName ? `${cc.actorName}发卡` : "获得新卡";
      out.push({ id: `card-${cc.id}`, at: cc.obtainedAt, color: C.purpleBright, text: `${who}「${card.name}」· ${rarityStyle(card.rarity).name}`, points: takePoints((e) => e.sourceId === cc.id) });
    }

    for (const r of Object.values(redemptions)) {
      if (r.childId !== child.id || !isToday(r.requestedAt)) continue;
      out.push({ id: `red-${r.id}`, at: r.requestedAt, color: C.accent, text: `申请兑换「${r.rewardName}」` });
    }

    for (const e of ledger) {
      const who = e.sourceType === "manual" && e.actorName ? `${e.actorName}：` : "";
      out.push({ id: `pt-${e.id}`, at: e.createdAt, color: C.accentShadow, text: who + e.reason, points: e.delta });
    }

    return out.sort((a, b) => b.at - a.at).slice(0, 30);
  }, [cal, today, child.id, reminderLogs, assignments, contents, childCards, cards, redemptions, pointLedger]);

  return (
    <section className="home-feed" aria-label="今天动态">
      <h2>今天动态</h2>
      {items.length === 0 && <p className="home-feed-empty">今天还没有动态</p>}
      <ul>
        {items.map((it) => (
          <li key={it.id}>
            <i style={{ background: it.color }} />
            <p>
              {it.text}
              {it.points != null && <b className="kp-num"> {signed(it.points)}</b>}
            </p>
            <time className="kp-num">{cal.timeString(it.at)}</time>
          </li>
        ))}
      </ul>
    </section>
  );
}
